'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

function _interopDefault (ex) { return (ex && (typeof ex === 'object') && 'default' in ex) ? ex['default'] : ex; }

var React = _interopDefault(require('react'));
var getContentStateFragment = _interopDefault(require('draft-js/lib/getContentStateFragment'));
var getDraftEditorSelection = _interopDefault(require('draft-js/lib/getDraftEditorSelection'));
var draftJs = require('draft-js');

// @flow
/*:: import type { BlockNode } from "draft-js/lib/BlockNode.js.flow";*/

// Default maximum block depth supported by Draft.js CSS.
var DRAFT_DEFAULT_MAX_DEPTH = 4; // Default depth class prefix from Draft.js CSS.

var DRAFT_DEFAULT_DEPTH_CLASS = "public-DraftStyleDefault-depth";
/**
 * Generates CSS styles for list items, for a given selector pattern.
 * @param {string} selectorPrefix
 * @param {number} minDepth
 * @param {number} maxDepth
 */

var generateListNestingStyles = function generateListNestingStyles(selectorPrefix
/*: string*/
, minDepth
/*: number*/
, maxDepth
/*: number*/
) {
  var styles = "";

  for (var depth = minDepth; depth <= maxDepth; depth++) {
    var d = String(depth);
    var prefix = "".concat(selectorPrefix).concat(d);
    var counter = "ol".concat(d);
    var margin = 1.5 * (depth + 1);
    var m = String(margin);
    styles += "\n.".concat(prefix, ".public-DraftStyleDefault-listLTR { margin-left: ").concat(m, "em; }\n.").concat(prefix, ".public-DraftStyleDefault-listRTL { margin-right: ").concat(m, "em; }\n.").concat(prefix, ".public-DraftStyleDefault-orderedListItem::before { content: counter(").concat(counter, ") '. '; counter-increment: ").concat(counter, "; }\n.").concat(prefix, ".public-DraftStyleDefault-reset { counter-reset: ").concat(counter, "; }");
  }

  return styles;
};
/**
 * Dynamically generates the right list nesting styles.
 * Can be wrapped as a pure component - to re-render only when `max` changes (eg. never).
 */

var ListNestingStyles = function ListNestingStyles(props
/*: { max: number }*/
) {
  var max = props.max;
  var min = DRAFT_DEFAULT_MAX_DEPTH + 1;
  return max > DRAFT_DEFAULT_MAX_DEPTH ? React.createElement("style", null, generateListNestingStyles(DRAFT_DEFAULT_DEPTH_CLASS, min, max)) : null;
};
/**
 * Add depth classes that Draft.js doesn't provide.
 * See https://github.com/facebook/draft-js/blob/232791a4e92d94a52c869f853f9869367bdabdac/src/component/contents/DraftEditorContents-core.react.js#L58-L62.
 * @param {ContentBlock} block
 */

var blockDepthStyleFn = function blockDepthStyleFn(block
/*: BlockNode*/
) {
  var depth = block.getDepth();
  return depth > DRAFT_DEFAULT_MAX_DEPTH ? "".concat(DRAFT_DEFAULT_DEPTH_CLASS).concat(String(depth)) : "";
};

// @flow
/*:: import type { ElementRef } from "react";*/

/*:: import type { Editor, EditorState as EditorStateType } from "draft-js";*/

// Custom attribute to store Draft.js content in the HTML clipboard.
var FRAGMENT_ATTR = "data-draftjs-conductor-fragment";
var DRAFT_DECORATOR = '[data-contents="true"] [contenteditable="false"]'; // Checks whether the selection is inside a decorator or not.
// This is important to change the copy-cut behavior accordingly.

var isSelectionInDecorator = function isSelectionInDecorator(selection
/*: Selection*/
) {
  var anchorNode = selection.anchorNode,
      focusNode = selection.focusNode;

  if (!anchorNode || !focusNode) {
    return false;
  }

  var anchor = anchorNode instanceof Element ? anchorNode : anchorNode.parentElement;
  var focus = focusNode instanceof Element ? focusNode : focusNode.parentElement;
  var anchorDecorator = anchor && anchor.closest(DRAFT_DECORATOR);
  var focusDecorator = focus && focus.closest(DRAFT_DECORATOR);
  return anchorDecorator && focusDecorator && (anchorDecorator.contains(focusDecorator) || focusDecorator.contains(anchorDecorator));
}; // Get clipboard content from the selection like Draft.js would.


var getSelectedContent = function getSelectedContent(editorState
/*: EditorStateType*/
, editorRoot
/*: HTMLElement*/
) {
  var _getDraftEditorSelect = getDraftEditorSelection(editorState, editorRoot),
      selectionState = _getDraftEditorSelect.selectionState;

  var fragment = getContentStateFragment(editorState.getCurrentContent(), selectionState); // If the selection contains no content (according to Draft.js), use the default browser behavior.
  // This happens when selecting text that's within contenteditable=false blocks in Draft.js.
  // See https://github.com/thibaudcolas/draftjs-conductor/issues/12.

  var isEmpty = fragment.every(function (block) {
    return block.getText().length === 0;
  });
  return isEmpty ? null : fragment;
}; // Overrides the default copy/cut behavior, adding the serialised Draft.js content to the clipboard data.
// See also https://github.com/basecamp/trix/blob/62145978f352b8d971cf009882ba06ca91a16292/src/trix/controllers/input_controller.coffee#L415-L422
// We serialise the editor content within HTML, not as a separate mime type, because Draft.js only allows access
// to HTML in its paste event handler.


var draftEditorCopyListener = function draftEditorCopyListener(ref
/*: ElementRef<Editor>*/
, e
/*: Event & {
    clipboardData: DataTransfer,
  }*/
) {
  var selection = window.getSelection(); // Completely skip event handling if clipboardData is not supported (IE11 is out).
  // Also skip if there is no selection ranges.
  // Or if the selection is fully within a decorator.

  if (!e.clipboardData || selection.rangeCount === 0 || isSelectionInDecorator(selection)) {
    return;
  }

  var fragment = getSelectedContent(ref._latestEditorState, ref.editor); // Override the default behavior if there is selected content.

  if (fragment) {
    var content = draftJs.ContentState.createFromBlockArray(fragment.toArray());
    var serialisedContent = JSON.stringify(draftJs.convertToRaw(content)); // Create a temporary element to store the selection’s HTML.
    // See also Rangy's implementation: https://github.com/timdown/rangy/blob/1e55169d2e4d1d9458c2a87119addf47a8265276/src/core/domrange.js#L515-L520.

    var fragmentElt = document.createElement("div"); // Modern browsers only support a single range.

    fragmentElt.appendChild(selection.getRangeAt(0).cloneContents());
    fragmentElt.setAttribute(FRAGMENT_ATTR, serialisedContent); // We set the style property to replicate the browser's behavior of inline styles in rich text copy-paste.
    // In Draft.js, this is important for line breaks to be interpreted correctly when pasted into another word processor.
    // See https://github.com/facebook/draft-js/blob/a1f4593d8fa949954053e5d5840d33ce1d1082c6/src/component/base/DraftEditor.react.js#L328.

    fragmentElt.setAttribute("style", "white-space: pre-wrap;");
    e.clipboardData.setData("text/plain", selection.toString());
    e.clipboardData.setData("text/html", fragmentElt.outerHTML);
    e.preventDefault();
  }
};
/**
 * Registers custom copy/cut event listeners on an editor.
 */


var registerCopySource = function registerCopySource(ref
/*: ElementRef<Editor>*/
) {
  var editorElt = ref.editor;
  var onCopy = draftEditorCopyListener.bind(null, ref);
  editorElt.addEventListener("copy", onCopy);
  editorElt.addEventListener("cut", onCopy);
  return {
    unregister: function unregister() {
      editorElt.removeEventListener("copy", onCopy);
      editorElt.removeEventListener("cut", onCopy);
    }
  };
};
/**
 * Handles pastes coming from Draft.js editors set up to serialise
 * their Draft.js content within the HTML.
 * This SHOULD NOT be used for stripPastedStyles editor.
 */

var handleDraftEditorPastedText = function handleDraftEditorPastedText(html
/*: ?string*/
, editorState
/*: EditorStateType*/
) {
  // Plain-text pastes are better handled by Draft.js.
  if (html === "" || typeof html === "undefined" || html === null) {
    return false;
  }

  var doc = new DOMParser().parseFromString(html, "text/html");
  var fragmentElt = doc.querySelector("[".concat(FRAGMENT_ATTR, "]")); // Handle the paste if it comes from draftjs-conductor.

  if (fragmentElt) {
    var fragmentAttr = fragmentElt.getAttribute(FRAGMENT_ATTR);
    var rawContent;

    try {
      // If JSON parsing fails, leave paste handling to Draft.js.
      // There is no reason for this to happen, unless the clipboard was altered somehow.
      // $FlowFixMe
      rawContent = JSON.parse(fragmentAttr);
    } catch (error) {
      return false;
    }

    var fragment = draftJs.convertFromRaw(rawContent).getBlockMap();
    var content = draftJs.Modifier.replaceWithFragment(editorState.getCurrentContent(), editorState.getSelection(), fragment);
    return draftJs.EditorState.push(editorState, content, "insert-fragment");
  }

  return false;
};

exports.DRAFT_DEFAULT_DEPTH_CLASS = DRAFT_DEFAULT_DEPTH_CLASS;
exports.DRAFT_DEFAULT_MAX_DEPTH = DRAFT_DEFAULT_MAX_DEPTH;
exports.ListNestingStyles = ListNestingStyles;
exports.blockDepthStyleFn = blockDepthStyleFn;
exports.generateListNestingStyles = generateListNestingStyles;
exports.handleDraftEditorPastedText = handleDraftEditorPastedText;
exports.registerCopySource = registerCopySource;



//////////////////
// WEBPACK FOOTER
// ./~/draftjs-conductor/dist/draftjs-conductor.cjs.js
// module id = 531
// module chunks = 0